import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiActivity,
  FiFileText,
  FiUsers,
  FiLayers,
  FiHome,
  FiBookOpen,
  FiAward,
  FiMessageCircle,
  FiHeart,
  FiStar,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiClock,
} from "react-icons/fi";
import "./ActivityTab.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Activity type configurations - Compatible with backend API response format
// Backend Activity Types per FRONTEND-PROFILE-GUIDE.md Section 9:
// post_created, room_created, room_joined, community_created, community_joined,
// workshop_created, workshop_joined, rating_given, rating_received,
// skill_endorsed, skill_received, collaboration_requested, collaboration_received
const activityConfig = {
  post_created: {
    icon: FiFileText,
    color: "var(--prf-blue)",
    label: "Created a post",
  },
  room_joined: {
    icon: FiHome,
    color: "var(--prf-green)",
    label: "Joined a research space",
  },
  room_created: {
    icon: FiLayers,
    color: "var(--prf-purple)",
    label: "Created a research space",
  },
  community_joined: {
    icon: FiUsers,
    color: "var(--prf-indigo)",
    label: "Joined a community",
  },
  community_created: {
    icon: FiUsers,
    color: "var(--prf-indigo)",
    label: "Created a community",
  },
  workshop_created: {
    icon: FiBookOpen,
    color: "var(--prf-amber)",
    label: "Created a workshop",
  },
  workshop_joined: {
    icon: FiBookOpen,
    color: "var(--prf-amber)",
    label: "Joined a workshop",
  },
  skill_endorsed: {
    icon: FiAward,
    color: "var(--prf-pink)",
    label: "Endorsed a skill",
  },
  skill_received: {
    icon: FiAward,
    color: "var(--prf-pink)",
    label: "Received skill endorsement",
  },
  rating_given: {
    icon: FiStar,
    color: "var(--prf-amber)",
    label: "Gave a rating",
  },
  rating_received: {
    icon: FiStar,
    color: "var(--prf-amber)",
    label: "Received a rating",
  },
  collaboration_requested: {
    icon: FiUsers,
    color: "var(--prf-green)",
    label: "Sent collaboration request",
  },
  collaboration_received: {
    icon: FiUsers,
    color: "var(--prf-green)",
    label: "Received collaboration request",
  },
  comment_added: {
    icon: FiMessageCircle,
    color: "var(--prf-cyan)",
    label: "Commented on a post",
  },
  post_liked: {
    icon: FiHeart,
    color: "var(--prf-red)",
    label: "Liked a post",
  },
  review_received: {
    icon: FiStar,
    color: "var(--prf-amber)",
    label: "Received a review",
  },
  default: {
    icon: FiActivity,
    color: "var(--prf-slate-500)",
    label: "Activity",
  },
};

// Filter options
const filterOptions = [
  { id: "all", label: "All Activity" },
  { id: "posts", label: "Posts" },
  { id: "rooms", label: "Research Spaces" },
  { id: "communities", label: "Communities" },
  { id: "workshops", label: "Workshops" },
  { id: "endorsements", label: "Endorsements" },
];

// Timeline Item Component
const TimelineItem = ({ activity, isLast }) => {
  const config =
    activityConfig[activity.type?.toLowerCase()] || activityConfig.default;
  const Icon = config.icon;

  const formatDate = (date) => {
    if (!date) return "Recently";
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;

    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;

    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatFullDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <motion.div className="act-timeline-item" variants={itemVariants}>
      {/* Timeline Line */}
      <div className="act-timeline-line">
        <div
          className="act-timeline-dot"
          style={{ backgroundColor: config.color }}
        >
          <Icon />
        </div>
        {!isLast && <div className="act-timeline-connector" />}
      </div>

      {/* Content */}
      <div className="act-timeline-content">
        <div className="act-activity-card">
          <div className="act-activity-header">
            <span className="act-activity-type" style={{ color: config.color }}>
              {config.label}
            </span>
            <span
              className="act-activity-time"
              title={formatFullDate(activity.createdAt)}
            >
              {formatDate(activity.createdAt)}
            </span>
          </div>

          <div className="act-activity-body">
            <p className="act-activity-desc">
              {activity.action ||
                activity.description ||
                activity.type?.replace(/_/g, " ")}
            </p>

            {/* Related Item */}
            {activity.data && (
              <div className="act-activity-related">
                {activity.data.thumbnail && (
                  <img
                    src={activity.data.thumbnail}
                    alt=""
                    className="act-related-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <div className="act-related-info">
                  <span className="act-related-title">
                    {activity.data.name || activity.data.title}
                  </span>
                  {activity.data.subtitle && (
                    <span className="act-related-subtitle">
                      {activity.data.subtitle}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Stats Overview Component
const StatsOverview = ({ activities }) => {
  const stats = useMemo(() => {
    if (!activities?.length) return [];

    const thisWeek = activities.filter((a) => {
      const d = new Date(a.createdAt);
      const week = new Date();
      week.setDate(week.getDate() - 7);
      return d > week;
    }).length;

    const thisMonth = activities.filter((a) => {
      const d = new Date(a.createdAt);
      const month = new Date();
      month.setMonth(month.getMonth() - 1);
      return d > month;
    }).length;

    return [
      {
        label: "Total Activities",
        value: activities.length,
        icon: FiActivity,
        color: "var(--prf-blue)",
      },
      {
        label: "This Week",
        value: thisWeek,
        icon: FiCalendar,
        color: "var(--prf-green)",
      },
      {
        label: "This Month",
        value: thisMonth,
        icon: FiClock,
        color: "var(--prf-purple)",
      },
    ];
  }, [activities]);

  return (
    <div className="act-stats">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            className="act-stat-card"
            variants={itemVariants}
            style={{ "--stat-color": stat.color }}
          >
            <div className="act-stat-icon">
              <Icon />
            </div>
            <div className="act-stat-info">
              <span className="act-stat-value">{stat.value}</span>
              <span className="act-stat-label">{stat.label}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// Date Group Header
const DateGroupHeader = ({ date }) => {
  const formatGroupDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";

    return d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <div className="act-date-header">
      <span className="act-date-text">{formatGroupDate(date)}</span>
    </div>
  );
};

// Empty State
const EmptyState = ({ isOwnProfile }) => (
  <motion.div className="act-empty" variants={itemVariants}>
    <div className="act-empty-icon">
      <FiActivity />
    </div>
    <h3 className="act-empty-title">No activity yet</h3>
    <p className="act-empty-text">
      {isOwnProfile
        ? "Your activity will appear here as you interact with the platform"
        : "This user's activity will appear here"}
    </p>
  </motion.div>
);

// Main Component
const ActivityTab = ({ activityFeed = [], isOwnProfile }) => {
  const [filter, setFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Filter activities
  const filteredActivities = useMemo(() => {
    if (filter === "all") return activityFeed;

    const filterMap = {
      posts: ["post_created", "post_liked", "comment_added"],
      rooms: ["room_joined", "room_created"],
      communities: ["community_joined"],
      workshops: ["workshop_joined", "workshop_created"],
      endorsements: ["skill_endorsed", "review_received"],
    };

    const types = filterMap[filter] || [];
    return activityFeed.filter((a) => types.includes(a.type?.toLowerCase()));
  }, [activityFeed, filter]);

  // Group activities by date
  const groupedActivities = useMemo(() => {
    const groups = {};

    filteredActivities.forEach((activity) => {
      const date = activity.createdAt
        ? new Date(activity.createdAt).toDateString()
        : "Unknown";
      if (!groups[date]) groups[date] = [];
      groups[date].push(activity);
    });

    return Object.entries(groups).sort(
      (a, b) => new Date(b[0]) - new Date(a[0])
    );
  }, [filteredActivities]);

  const currentFilter = filterOptions.find((f) => f.id === filter);

  return (
    <motion.div
      className="act-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Stats Overview */}
      <StatsOverview activities={activityFeed} />

      {/* Toolbar */}
      <motion.div className="act-toolbar" variants={itemVariants}>
        <div className="act-toolbar-title">
          <FiActivity className="act-toolbar-icon" />
          <h3>Activity Timeline</h3>
        </div>

        {/* Filter Dropdown */}
        <div className="act-filter-wrapper">
          <motion.button
            className="act-filter-btn"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiFilter />
            <span>{currentFilter?.label}</span>
            <FiChevronDown
              className={`act-chevron ${showFilterDropdown ? "open" : ""}`}
            />
          </motion.button>

          <AnimatePresence>
            {showFilterDropdown && (
              <motion.div
                className="act-filter-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {filterOptions.map((option) => (
                  <button
                    key={option.id}
                    className={`act-filter-option ${
                      filter === option.id ? "active" : ""
                    }`}
                    onClick={() => {
                      setFilter(option.id);
                      setShowFilterDropdown(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Timeline Content */}
      <div className="act-timeline">
        {groupedActivities.length === 0 ? (
          <EmptyState isOwnProfile={isOwnProfile} />
        ) : (
          groupedActivities.map(([date, activities]) => (
            <motion.div
              key={date}
              className="act-date-group"
              variants={containerVariants}
            >
              <DateGroupHeader date={date} />
              {activities.map((activity, index) => (
                <TimelineItem
                  key={activity._id || index}
                  activity={activity}
                  isLast={index === activities.length - 1}
                />
              ))}
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default ActivityTab;
